import React from 'react';
import MakePayment from '../MakePayment';
import apiClient from "../../../../services/api-client";
import {Divider} from 'primereact/divider';
import AppCard from '../../../../components/ui/AppCard';
import InfoField from '../../../../components/ui/InfoField';
import StatusBadge from '../../../../components/ui/StatusBadge';
import localBGImage from "../../../../assets/images/patterns/applyPolicy.png"
import {useHistory} from 'react-router-dom';
import {Skeleton} from 'primereact/skeleton';
import {usePolicyTracking} from '../../hooks/usePolicyTracking';
import PolicyPaymentHistory from './PolicyPaymentHistoryTable'
import Menu from '../../../../components/common/Menu';
import { getPolicyStatusLabel, getPolicyStatusTone, getPolicyStatusMessage } from '../../../policies/utils/policyStatus';
import { policyMenuTrigger } from '../../../policies/components/policyMenuTrigger';
import StorageHelper from '../../../../utils/storage-helper';

function NewApplicationPolicyDisplay(response) {
  const history = useHistory();
  return (
    <div className="w-full mb-8">
      <div className="rounded-2xl overflow-hidden shadow-sm border border-base-200 bg-base-100">
        <img src={localBGImage} alt="Embrace peace of mind" className="w-full object-cover" />
        <div className="p-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-white">
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Create your Doves Life Policy now!</h3>
            <p className="text-slate-600">
              <strong className="text-slate-800">For the ones you love</strong><br />
              Create your life policy today and embrace a peace of mind.
            </p>
          </div>
          <button 
            className="btn btn-info px-8 text-white rounded-full shadow-sm hover:shadow-md transition-shadow whitespace-nowrap"
            onClick={() => { history.push("/manage-policy?category=Funeral policy") }}
          >
            Apply Now
          </button>
        </div>
      </div>
    </div>
  );
}

function EasipolPolicyDisplay({ policyList, policyDisplayKey, apiResponse, updateSelectedPolicy }) {
  const currentMember = policyList?.[policyDisplayKey]?.MainMember;
  const statusLabel = getPolicyStatusLabel(currentMember?.Status);
  const statusTone = getPolicyStatusTone(currentMember?.Status);
  const statusMessage = getPolicyStatusMessage(currentMember);

  
  return (
    <>
      {policyList ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
          <AppCard headerImg={localBGImage} className="h-full flex flex-col" bodyClassName="flex-1 flex flex-col">
            <div className="mb-4">
              <span className="badge badge-info bg-blue-100 text-blue-700 border-none badge-sm uppercase font-bold mb-3 px-3 py-3 rounded-full tracking-wider text-xs">Policy Summary</span>
              <h2 className="text-2xl font-extrabold text-base-content mb-2 tracking-tight">Your Policy Details</h2>
              <p className="text-sm text-base-content/70">Review the selected policy and member information.</p>
            </div>
            <Divider type="solid" className="opacity-30 my-4" />

            <div className="mb-6">
              <Menu
                  className="border border-base-300 rounded-xl px-4 py-3 w-full bg-base-100 flex justify-between items-center cursor-pointer shadow-sm"
                  trigger={policyMenuTrigger(currentMember.Policy_Number)}
                  items={policyList}
                  getKey={(p) => p.MainMember.Policy_Number}
                  searchable={policyList.length > 5}
                  searchPlaceholder="Search policy number"
                  filterItem={(p, q) =>
                      p.MainMember.Policy_Number.toLowerCase().includes(q.toLowerCase())
                  }
                  renderItem={(p) => (
                      <span className="flex justify-between items-center w-full py-2 border-b border-base-200 last:border-0">
                      <span className="font-semibold text-base-content">
                        {p.MainMember.Policy_Number}
                      </span>
                    </span>
                  )}
                  onSelect={(p) => updateSelectedPolicy(p, policyList, apiResponse)}
              />
            </div>

            <div className="mb-6 bg-base-200 rounded-xl p-4">
              <span className="text-xs uppercase font-bold text-base-content/60 block mb-1">Principal Member</span>
              <strong className="text-lg">{currentMember.Title} {currentMember.First_Name} {currentMember.Surname}</strong>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <InfoField label="Monthly Premium" value={`$ ${currentMember.UsualPremium ?? "N/A"}`} />
              <InfoField
                  label="Policy Status"
                  value={<StatusBadge tone={statusTone} label={statusLabel} message={statusMessage} />}
              />
              <InfoField label="Policy Number" value={currentMember.Policy_Number} />
              <InfoField label="Policy Product" value={currentMember.Product_Name} />
            </div>

            <div className="mb-6 bg-base-200 rounded-xl p-4">
              <span className="text-xs uppercase font-bold text-base-content/60 block mb-1">Address</span>
              <strong className="block">{currentMember.Address}</strong>
              <small className="text-base-content/70">{currentMember.Region}</small>
            </div>
          </AppCard>

          <MakePayment
              balanceFormatted={policyList[policyDisplayKey].MainMember.UsualPremium}
              policyNumberCurrentUser={policyList[policyDisplayKey].MainMember.Policy_Number}
              phoneNumberCurrentUser={policyList[policyDisplayKey].MainMember.Cell_Number}
              policyList={policyList}
          />
          
          <div className="lg:col-span-2">
            <PolicyPaymentHistory balanceFormatted={policyList[policyDisplayKey].MainMember} />
          </div>
        </div>
      ) : (
        <h5 className="font-bold">Select policy to display</h5>
      )}
    </>
  );
}

function PendingApplicationPolicyDisplay({ applicationDetail, deleteApplication }) {
  const history = useHistory();
    switch (applicationDetail.applicationStatus) {
      case 'waiting-approval':
        return (
          <div className="w-full mb-8">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-base-200 bg-base-100">
              <img src={localBGImage} alt="Embrace peace of mind" className="w-full object-cover" />
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Your Policy is being reviewed</h3>
                <p className="text-slate-600">
                  <strong className="text-slate-800">We will be in touch in a moment</strong><br />
                  We embrace operational excellence at all times, gained by our years of extensive experience.
                </p>
              </div>
            </div>
          </div>
        );
      case 'incomplete-application':
        return (
          <div className="w-full mb-8">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-base-200 bg-base-100">
              <img src={localBGImage} alt="Embrace peace of mind" className="w-full object-cover" />
              <div className="p-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-white">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">You have not completed your policy application</h3>
                  <p className="text-slate-600">
                    <strong className="text-slate-800">You can</strong> continue from where you left off and finish your application.
                  </p>
                  <p className="text-error font-medium mt-1"><i>Kindly finish this application or delete it to access more functionality.</i></p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button 
                    className="btn btn-warning px-6 rounded-full shadow-sm"
                    onClick={() => { history.push("/manage-policy") }}
                  >
                    Continue Application
                  </button>
                  <button 
                    className="btn btn-error btn-outline px-6 rounded-full"
                    onClick={() => { deleteApplication(applicationDetail.mainMember.ID_Number) }}
                  >
                    Delete Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      case 'rejected-policy-application':
        return (
          <div className="w-full mb-8">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-base-200 bg-base-100">
              <img src={localBGImage} alt="Embrace peace of mind" className="w-full object-cover" />
              <div className="p-8 flex flex-col md:flex-row items-center justify-between gap-6 bg-white">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Your Policy might have concerns that need you to address them.</h3>
                  <p className="text-slate-600">
                    <strong className="text-slate-800">For the ones you love</strong><br />
                    We embrace operational excellence at all times, gained by our years of extensive experience.
                  </p>
                </div>
                <button 
                  className="btn btn-warning px-8 rounded-full shadow-sm text-white"
                  onClick={() => { history.push("/manage-policy") }}
                >
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="w-full mb-8">
            <div className="rounded-2xl overflow-hidden shadow-sm border border-base-200 bg-base-100">
              <img src={localBGImage} alt="Embrace peace of mind" className="w-full object-cover" />
              <div className="p-8 bg-white">
                <h3 className="text-2xl font-bold text-slate-800 mb-2">Your Policy application is being processed</h3>
                <p className="text-slate-600">
                  <strong className="text-slate-800">For the ones you love</strong><br />
                  We embrace operational excellence at all times, gained by our years of extensive experience.
                </p>
              </div>
            </div>
          </div>
        );
    }
  }

const BalanceWidget = () => {
  const deleteApplication = async (ID_Number) => {
    try {
      const response = await apiClient.get('policy/track/' + ID_Number + "/delete", {
        headers: {
          Authorization: 'Bearer ' + (StorageHelper.get('token'))
        }
      });
      if (response.data.status === "SUCCESS") {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error clearing balance details:', error);
    }
  };

  const renderTrackResponse = (apiResponse, policyDisplayKey, { updateSelectedPolicy }) => {
    if (apiResponse?.responseBody && !(Array.isArray(apiResponse.responseBody)) && apiResponse.responseBody.policyApplicationRequestId) {
      return <div className="row">
          <div className="col-xxl-12 col-xl-12 col-lg-12">
            <PendingApplicationPolicyDisplay applicationDetail={apiResponse.responseBody} deleteApplication={deleteApplication} />
          </div>
        </div>;
    }

    if (apiResponse?.responseBody && (Array.isArray(apiResponse.responseBody)) && apiResponse.responseBody.length > 0) {
      return <EasipolPolicyDisplay key={policyDisplayKey} policyList={apiResponse.responseBody} policyDisplayKey={policyDisplayKey} apiResponse={apiResponse} updateSelectedPolicy={updateSelectedPolicy} />;
    }

    return <NewApplicationPolicyDisplay applicationDetail={apiResponse.responseBody} />;
  };

  const { policyDisplay, loading } = usePolicyTracking(renderTrackResponse);

  return (
    <div className="row">
        {loading ? (
          <div className="border-round border-1 surface-border p-4 surface-card">
            <div className="flex mb-3">
              <Skeleton shape="circle" size="4rem" className="mr-2"></Skeleton>
              <div>
                <Skeleton width="10rem" className="mb-2"></Skeleton>
                <Skeleton width="5rem" className="mb-2"></Skeleton>
                <Skeleton height=".5rem"></Skeleton>
              </div>
            </div>
            <Skeleton width="100%" height="150px"></Skeleton>
            <div className="flex justify-content-between mt-3">
              <Skeleton width="4rem" height="2rem"></Skeleton>
              <Skeleton width="4rem" height="2rem"></Skeleton>
            </div>
          </div>) :
          <div className="col-xxl-12 col-xl-12 col-lg-12">

            {policyDisplay}
          </div>}
      </div>
  );
};

export default BalanceWidget;
